import { isNonNullish } from 'remeda'
import type {
  AdditionalCartInfoProductConnectionData,
  CartData,
  TransformedCartData,
} from '@velasca/types'
import { getFeaturedImage } from './getFeaturedImage'
import { isMediaImage } from './isMediaImage'
import { normalizeText } from './normalizeText'

/**
 * Transforming cart.js data with desired form with additional data fetched by gql.
 *
 */
export const transformCart = (
  cartData: CartData | null,
  additionalCartInfo?: AdditionalCartInfoProductConnectionData | null,
): TransformedCartData | null => {
  if (!cartData) {
    return null
  }

  // Merge data and cartData. I need to do this because cart.js doesn't provide the image I want
  const transformedItems = cartData.items.map((cartItem) => {
    const productAdditionalInfo = additionalCartInfo?.products.edges.find(
      ({ node }) => node.id.endsWith(cartItem.product_id.toString()),
    )?.node

    const transformedProps = {
      id: cartItem.id.toString(),
      variant_id: cartItem.variant_id.toString(),
      product_type: cartItem.gift_card ? null : cartItem.product_type,
      options: {
        size:
          cartItem.gift_card || cartItem.options_with_values.length === 1
            ? null
            : cartItem.options_with_values[0]?.value,
        color: cartItem.gift_card
          ? null
          : cartItem.options_with_values[1]?.value,
        material: cartItem.gift_card
          ? null
          : cartItem.options_with_values[2]?.value,
      },
    }

    if (!productAdditionalInfo) {
      return {
        ...cartItem,
        ...transformedProps,
        image: {
          loading: false,
          error: true,
          data: null,
        },
      }
    }

    const {
      media,
      customProductType,
      frontImageIndex,
      compareAtPriceRange,
      crossSellProductsEditorialImage,
      seoCaption,
    } = productAdditionalInfo

    const flatImages = media.edges
      .map(({ node }) => node)
      .filter(isMediaImage)
      .map(({ image }) => image?.url)
      .filter(isNonNullish)
      .map((image) => ({ src: image }))

    const crossSellImageReference = crossSellProductsEditorialImage?.reference

    return {
      ...cartItem,
      ...transformedProps,
      compareAtPrice: {
        max: parseFloat(compareAtPriceRange.maxVariantPrice.amount || '0'),
        min: parseFloat(compareAtPriceRange.minVariantPrice.amount || '0'),
      },
      images: flatImages,
      image: {
        loading: false,
        error: false,
        data: getFeaturedImage(
          flatImages,
          cartItem.gift_card,
          cartItem.handle,
          customProductType?.value,
          frontImageIndex?.value,
        ),
      },
      crossSellProductsEditorialImage:
        crossSellImageReference && isMediaImage(crossSellImageReference)
          ? crossSellImageReference.image?.url
          : null,
      seoCaption: normalizeText(seoCaption?.value),
    }
  })

  return {
    ...cartData,
    items: transformedItems,
  }
}
